/* saif-course-merge.js — combine courses BEFORE the cart is shown, not after.
 *
 * Owner, 2026-08-05, watching two courses land as two lines and merge only on reload:
 *   "it doesnt combine them then refreshes and combines them we missed this case, so make it
 *    like the others where it combines before it shows in cart like the 3 course combo."
 *
 * The asymmetry he spotted:
 *   collection selector  -> saif-select.js settle()s the cart, THEN navigates. Arrives correct.
 *   course product page  -> adds one course and navigates. The cart page notices the overlap on
 *                           load, merges, and reloads. That second load is the flash.
 *
 * ⚠️ THERE IS NO ADD-TO-CART FORM ON A COURSE PAGE. The first version of this file intercepted
 * `form[action*="/cart/add"]` and would never have fired once: a course page has ZERO such forms
 * (checked in a real browser — the buy control is the selection bar, saif-select-bar with
 * data-fixed-count="1", driven by saif-select.js). So the converge is published here and CALLED
 * by saif-select.js's single-course branch, which is where a course-page add actually happens.
 *
 * ⚠️ THIS CONVERGES, IT DOES NOT SETTLE. saif-select.js deliberately refuses to settle on a
 * course page, and says why: "the visitor is adding one course to whatever they already hold —
 * settling there would delete a bundle they deliberately bought, silently." Converging only ever
 * replaces course lines the chosen bundle actually covers.
 *
 * ⚠️ It merges ONLY when the bundle is genuinely cheaper, comparing what the shopper is really
 * charged (final_line_price, discounts included) against the bundle price. Pair-merging was
 * switched off on 2026-08-01 because it "cost the shopper $24 in silence" while an automatic
 * discount made two singles cheaper than the pair. The price check means a re-created discount
 * makes this decline rather than overcharge.
 *
 * Every bail-out leaves the cart exactly as the add left it — correct, just with the old reload.
 */
(function () {
  var CATALOG = window.SaifCourseCatalog;
  if (!CATALOG || !CATALOG.singles || !CATALOG.byp) return;

  var WORDS = {
    'foundation-of-recitation': 'foundation',
    'precision-of-recitation': 'precision',
    'mastery-of-recitation': 'mastery'
  };
  var COURSE_HANDLES = Object.keys(WORDS);

  function root() {
    var b = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return b.charAt(b.length - 1) === '/' ? b : b + '/';
  }
  function post(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error(path + ' ' + r.status); return r.json(); });
  }

  /* Which bundle covers exactly this set of handles, and what it costs. */
  function bundleFor(handles) {
    if (handles.length === 3 && CATALOG.crp && CATALOG.crp.id) {
      return { id: CATALOG.crp.id, price: CATALOG.crp.price || 0 };
    }
    if (handles.length === 2) {
      var vs = CATALOG.byp.variants || [];
      for (var i = 0; i < vs.length; i++) {
        var t = String(vs[i].title || '').toLowerCase();
        var all = handles.every(function (h) { return t.indexOf(WORDS[h]) !== -1; });
        if (all) return { id: vs[i].id, price: vs[i].price || 0 };
      }
    }
    return null;
  }

  /* Read the cart; if its course lines converge on a CHEAPER bundle, make it so.
     Resolves true when it changed something, false when it deliberately did not. */
  function converge() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return false;

        var lines = [], seen = {}, paid = 0;
        for (var i = 0; i < cart.items.length; i++) {
          var it = cart.items[i];
          var p = it.properties || {};
          /* a gifted course is a legitimate separate line; a booking line carries the slot
             payload the worker needs to hold a reservation. Never touch either. */
          if (p._gift || p._booking_data) continue;
          /* a bundle already in the cart, or any line on a payment plan, belongs to the cart
             page — it preserves plans and handles covering lines. Leave the whole thing alone. */
          if (it.handle === CATALOG.byp.handle) return false;
          if (CATALOG.crp && it.handle === CATALOG.crp.handle) return false;
          if (it.selling_plan_allocation) return false;
          if (COURSE_HANDLES.indexOf(it.handle) === -1) continue;
          if (seen[it.handle]) continue;
          seen[it.handle] = true;
          lines.push(it);
          var lp = it.final_line_price;
          if (typeof lp !== 'number') lp = it.line_price;
          if (typeof lp !== 'number') lp = (it.price || 0) * (it.quantity || 1);
          paid += lp;
        }
        if (lines.length < 2) return false;

        var target = bundleFor(lines.map(function (l) { return l.handle; }));
        if (!target || !target.id || !(target.price > 0)) return false;

        /* strictly cheaper. Equal is not a saving, and merging for nothing only takes away the
           shopper's ability to remove one course on its own. */
        if (paid <= target.price) return false;

        /* ⚠️ ALL LINE KEYS in one update.js — never a variant id alongside a key naming the same
           variant, which returns 200 and EMPTIES the cart (proven 2026-08-04). The bundle is a
           separate add.js because update.js cannot attach a selling plan. */
        var updates = {};
        lines.forEach(function (l) { updates[l.key] = 0; });
        return post('cart/update.js', { updates: updates })
          .then(function () { return post('cart/add.js', { id: target.id, quantity: 1 }); })
          .then(function () {
            /* the cart shows the same notice its own normalizer uses when it converges */
            try { sessionStorage.setItem('saifCartCombined', '1'); } catch (e) {}
            return true;
          });
      })
      .catch(function () { return false; });
  }

  /* saif-select.js calls this in single-course mode, after its add and before it navigates. */
  window.SaifConvergeCart = converge;
})();
